import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, Phone } from "lucide-react";
import { DataTools, RowDelete } from "./DataTools";
import { api, formatApiError } from "@/lib/api";
import { Chips, Empty, Loading, PageHead, fmtWhen, money, useAdmin } from "./ui";
import { PAID, ReportFilters, SalesChart, SalesTable, SalesTotals, saleDay, ymd } from "./ShopSalesReport";
import { ReadyActions } from "./ReadyPickup";

const STATUSES = ["pending_payment", "reserved", "paid", "ready", "collected", "cancelled", "refunded"];
const label = (s) => s.replace("_", " ");
const METHODS = ["", "paypal", "card_in_store", "cash", "bank_transfer", "other"];

const OrderRow = ({ o, onChange }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(o.admin_notes || "");
  const patch = async (body, msg) => {
    try { await api.patch(`/admin/orders/${o.id}`, body); toast.success(msg); onChange(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className={`req st-${o.status}`} data-testid={`order-${o.id}`}>
      <button type="button" className="order-head" onClick={() => setOpen(!open)} data-testid="order-toggle">
        <span><strong>{o.number}</strong> · {o.name}</span>
        <span className={`pill st-pill-${o.status}`}>{label(o.status)}</span>
        <small>{fmtWhen(o.created_at)} · {o.items.reduce((n, i) => n + i.qty, 0)} items · <b>{money(o.total)}</b>{o.payment_method && ` · ${label(o.payment_method)}`}</small>
      </button>
      <ReadyActions o={o} onChange={onChange} />
      {open && (
        <div className="order-body" data-testid="order-details">
          {o.items.map((i) => <p key={i.product_id} className="order-item">{i.qty} × {i.brand} {i.name} <b>{money(i.line_total)}</b></p>)}
          {o.notes && <p className="muted-sm">“{o.notes}”</p>}
          <div className="req-actions">
            <a className="btn light" href={`tel:${o.phone}`} data-testid="order-call-button"><Phone size={14} /> Call</a>
            <a className="btn light" href={`mailto:${o.email}?subject=${encodeURIComponent(`Your Wax Moi order ${o.number}`)}`} data-testid="order-email-button"><Mail size={14} /> Email</a>
          </div>
          <div className="order-ctl">
            <label className="mu-field">Status
              <select value={o.status} onChange={(e) => patch({ status: e.target.value }, "Status updated")} data-testid="order-status-select">
                {STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
              </select>
            </label>
            <label className="mu-field">Paid by
              <select value={o.payment_method || ""} onChange={(e) => e.target.value && patch({ payment_method: e.target.value }, "Payment method saved")} data-testid="order-method-select">
                {METHODS.map((m) => <option key={m} value={m}>{m ? label(m) : "—"}</option>)}
              </select>
            </label>
          </div>
          <textarea rows={2} placeholder="Private notes" value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== (o.admin_notes || "") && patch({ admin_notes: notes }, "Notes saved")} data-testid="order-notes-input" />
          <p className="muted-sm">“Ready for pickup” emails the client straight away; you can then send the same message by WhatsApp or text.</p>
          <RowDelete url={`/admin/orders/${o.id}`} name={`order ${o.number}`} onDone={onChange} testId="order-delete-button" />
        </div>
      )}
    </div>
  );
};

const BestSellers = ({ orders }) => {
  const top = Object.values(orders.filter((o) => PAID.includes(o.status)).flatMap((o) => o.items).reduce((m, i) => {
    const k = `${i.brand} ${i.name}`.trim();
    m[k] = { name: k, qty: (m[k]?.qty || 0) + i.qty, revenue: (m[k]?.revenue || 0) + i.line_total };
    return m;
  }, {})).sort((a, b) => b.revenue - a.revenue).slice(0, 5);
  if (!top.length) return null;
  return (
    <div className="ad-card" data-testid="orders-top-products">
      <h3>Best sellers</h3>
      {top.map((t) => <div className="ad-row" key={t.name}><span>{t.name} × {t.qty}</span><strong>{money(t.revenue)}</strong></div>)}
    </div>
  );
};

const monthAgo = () => { const d = new Date(); d.setDate(d.getDate() - 29); return ymd(d); };

export default function ShopSales() {
  const { data } = useAdmin("orders", "/admin/orders");
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(() => ymd(new Date()));
  const [method, setMethod] = useState("all");
  const [view, setView] = useState("chart");
  const [filter, setFilter] = useState("all");
  const qc = useQueryClient();
  const inRange = useMemo(() => (data || []).filter((o) => {
    const d = saleDay(o);
    return d >= from && d <= to && (method === "all" || (method === "none" ? !o.payment_method : o.payment_method === method));
  }), [data, from, to, method]);
  const list = useMemo(() => inRange.filter((o) => filter === "all" || o.status === filter), [inRange, filter]);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin"] });
  if (!data) return <Loading />;
  return (
    <div data-testid="admin-shop-sales">
      <PageHead eyebrow="Revenue" title="Shop sales" />
      <p className="muted-sm">Product sales only — service deposits stay under Payments.</p>
      <ReportFilters {...{ from, to, setFrom, setTo, method, setMethod, view, setView }} />
      <SalesTotals orders={inRange} />
      {view === "chart" ? <SalesChart orders={inRange} from={from} to={to} /> : <SalesTable orders={inRange} from={from} to={to} />}
      <BestSellers orders={inRange} />
      <DataTools section="orders" label="shop orders" exportUrl={`/admin/orders/export?start=${from}&end=${to}`} filename={`waxmoi-shop-sales_${from}_${to}.xlsx`} onPurged={refresh} />
      <h3 className="sales-orders-title">Orders</h3>
      <Chips options={[["all", "All"], ...STATUSES.map((s) => [s, label(s)])]} value={filter} onChange={setFilter} testPrefix="orders-filter" />
      {!list.length ? <Empty>No shop orders in this range.</Empty> : <div className="req-list">{list.map((o) => <OrderRow key={o.id} o={o} onChange={refresh} />)}</div>}
    </div>
  );
}
